import { useState, useEffect } from 'react'
import {
  X, ArrowRight, CheckCircle, AlertTriangle, Repeat, Loader2, Building, Calendar, Clock
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { sansJpa, stagesDesJpa } from '../../../lib/jpa/stages.supabase'
import { useAuth } from '../../hooks/useAuth'

interface Center {
  id: string
  name: string
}

interface SessionResult {
  id: string
  stage_id: string
  center_id: string
  periode: string
  semaine: string
  start_date: string
  end_date: string
  capacity: number
  current_registrations: number
  stage: { id: string; title: string }
  center: { id: string; name: string }
}

interface RegistrationInput {
  id: string
  kid_id: string
  activity_id: string
  session: {
    id: string
    start_date: string
    end_date: string
    periode: string
    semaine: string
    center_id: string
    capacity: number
    current_registrations: number
    stage: { id: string; title: string }
    center: { id: string; name: string }
  }
}

interface Props {
  registration: RegistrationInput
  onClose: () => void
  onSuccess: () => void
}

export function GroupTransferModal({ registration, onClose, onSuccess }: Props) {
  const { userProfile } = useAuth()
  const src = registration.session

  const [centers, setCenters] = useState<Center[]>([])
  const [dstCenter, setDstCenter] = useState(src.center_id)
  const [dstPeriods, setDstPeriods] = useState<string[]>([])
  const [dstPeriod, setDstPeriod] = useState(src.periode)
  const [dstWeeks, setDstWeeks] = useState<string[]>([])
  const [dstWeek, setDstWeek] = useState(src.semaine)
  const [dstSessions, setDstSessions] = useState<SessionResult[]>([])
  const [selectedDstSession, setSelectedDstSession] = useState<SessionResult | null>(null)

  const [transferring, setTransferring] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [successTarget, setSuccessTarget] = useState('')

  useEffect(() => {
    supabase.from('centers').select('id, name').eq('active', true).order('name')
      .then(({ data }) => setCenters(data || []))
  }, [])

  useEffect(() => {
    setDstPeriod('')
    setDstWeek('')
    setDstSessions([])
    setSelectedDstSession(null)
    if (!dstCenter) { setDstPeriods([]); return }

    stagesDesJpa().catch(() => [] as string[])
      .then((jpa) => sansJpa(supabase.from('sessions').select('periode').eq('center_id', dstCenter).eq('archived', false), jpa))
      .then(({ data }) => {
        const unique = [...new Set((data || []).map(s => s.periode))].sort()
        setDstPeriods(unique)
        if (unique.includes(src.periode) && dstCenter === src.center_id) {
          setDstPeriod(src.periode)
        }
      })
  }, [dstCenter, src.center_id, src.periode])

  useEffect(() => {
    setDstWeek('')
    setDstSessions([])
    setSelectedDstSession(null)
    if (!dstCenter || !dstPeriod) { setDstWeeks([]); return }

    stagesDesJpa().catch(() => [] as string[])
      .then((jpa) => sansJpa(supabase.from('sessions').select('semaine')
        .eq('center_id', dstCenter).eq('periode', dstPeriod).eq('archived', false), jpa))
      .then(({ data }) => {
        const unique = [...new Set((data || []).map(s => s.semaine))].sort()
        setDstWeeks(unique)
        if (unique.includes(src.semaine) && dstCenter === src.center_id && dstPeriod === src.periode) {
          setDstWeek(src.semaine)
        }
      })
  }, [dstCenter, dstPeriod, src.center_id, src.periode, src.semaine])

  useEffect(() => {
    setDstSessions([])
    setSelectedDstSession(null)
    if (!dstCenter || !dstPeriod || !dstWeek) return

    stagesDesJpa().catch(() => [] as string[])
      .then((jpa) => sansJpa(supabase.from('sessions')
        .select(`
          id, stage_id, center_id, periode, semaine, start_date, end_date,
          capacity, current_registrations,
          stage:stages(id, title),
          center:centers(id, name)
        `)
        .eq('center_id', dstCenter)
        .eq('periode', dstPeriod)
        .eq('semaine', dstWeek)
        .eq('archived', false)
        .order('start_date'), jpa))
      .then(({ data }) => setDstSessions((data as any) || []))
  }, [dstCenter, dstPeriod, dstWeek])

  const availableSessions = dstSessions.filter(s => s.id !== src.id)

  const handleTransfer = async () => {
    if (!selectedDstSession) return
    setTransferring(true)
    setErrorMessage('')

    try {
      const { data: existing } = await supabase
        .from('registrations')
        .select('id')
        .eq('kid_id', registration.kid_id)
        .eq('activity_id', selectedDstSession.id)
        .neq('cancellation_status', 'cancelled_full_refund')
        .neq('cancellation_status', 'cancelled_no_refund')
        .neq('cancellation_status', 'cancelled_partial_refund')
        .limit(1)

      if (existing && existing.length > 0) {
        setErrorMessage('Cet enfant est deja inscrit dans la session de destination.')
        setTransferring(false)
        return
      }

      if (selectedDstSession.current_registrations >= selectedDstSession.capacity) {
        setErrorMessage('La session de destination est pleine.')
        setTransferring(false)
        return
      }

      const { error: updateError } = await supabase
        .from('registrations')
        .update({
          activity_id: selectedDstSession.id,
          subgroup_id: null,
          subgroup_name: null
        })
        .eq('id', registration.id)

      if (updateError) throw updateError

      await supabase
        .from('sessions')
        .update({ current_registrations: Math.max(0, src.current_registrations - 1) })
        .eq('id', src.id)

      await supabase
        .from('sessions')
        .update({ current_registrations: selectedDstSession.current_registrations + 1 })
        .eq('id', selectedDstSession.id)

      await supabase
        .from('registration_transfers')
        .insert({
          registration_id: registration.id,
          kid_id: registration.kid_id,
          from_session_id: src.id,
          to_session_id: selectedDstSession.id,
          transferred_by: userProfile?.id
        })

      setSuccessTarget(selectedDstSession.stage?.title || '')
      setShowSuccess(true)
    } catch (err) {
      console.error('Transfer error:', err)
      setErrorMessage('Erreur lors du transfert. Veuillez reessayer.')
    } finally {
      setTransferring(false)
    }
  }

  const handleCloseSuccess = () => {
    onSuccess()
  }

  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={handleCloseSuccess}>
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 flex flex-col items-center text-center animate-[scaleIn_0.3s_ease-out]"
          onClick={e => e.stopPropagation()}
        >
          <div className="relative mb-5">
            <svg className="h-20 w-20" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="36" fill="none" stroke="#d1fae5" strokeWidth="6" />
              <circle
                cx="40" cy="40" r="36" fill="none" stroke="#10b981" strokeWidth="6"
                strokeLinecap="round" strokeDasharray="226" strokeDashoffset="226"
                className="animate-[circleDraw_0.5s_0.2s_ease-out_forwards]"
              />
              <path
                d="M24 42 L35 53 L56 30" fill="none" stroke="#10b981" strokeWidth="5"
                strokeLinecap="round" strokeLinejoin="round"
                strokeDasharray="50" strokeDashoffset="50"
                className="animate-[checkDraw_0.4s_0.6s_ease-out_forwards]"
              />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Transfert effectue</h3>
          <p className="text-sm text-gray-600 mb-1">
            {src.stage?.title} <span className="mx-1">&rarr;</span> {successTarget}
          </p>
          <button
            onClick={handleCloseSuccess}
            className="mt-5 px-6 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Transfert d'inscription</h2>
            <p className="text-sm text-gray-500">Choisir la session de destination</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Source info */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-xs font-medium text-blue-600 mb-2">Inscription actuelle</p>
            <p className="text-sm font-medium text-blue-900">{src.stage?.title}</p>
            <p className="text-xs text-blue-700 mt-1">
              {src.center?.name} - {src.periode} - {src.semaine}
            </p>
            <p className="text-xs text-blue-600 mt-0.5">
              {new Date(src.start_date).toLocaleDateString('fr-BE')} au {new Date(src.end_date).toLocaleDateString('fr-BE')}
            </p>
          </div>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-red-800 text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Destination selectors */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Destination</h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1">
                  <Building className="h-3.5 w-3.5" /> Centre
                </label>
                <select
                  value={dstCenter}
                  onChange={e => setDstCenter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="">Selectionner</option>
                  {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1">
                  <Calendar className="h-3.5 w-3.5" /> Periode
                </label>
                <select
                  value={dstPeriod}
                  onChange={e => setDstPeriod(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="">Selectionner</option>
                  {dstPeriods.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1">
                  <Clock className="h-3.5 w-3.5" /> Semaine
                </label>
                <select
                  value={dstWeek}
                  onChange={e => setDstWeek(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="">Selectionner</option>
                  {dstWeeks.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Available sessions */}
          {dstWeek && availableSessions.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Sessions disponibles</label>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {availableSessions.map(session => {
                  const isFull = session.current_registrations >= session.capacity
                  return (
                    <button
                      key={session.id}
                      onClick={() => !isFull && setSelectedDstSession(session)}
                      disabled={isFull}
                      className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                        isFull
                          ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                          : selectedDstSession?.id === session.id
                            ? 'bg-amber-50 border-l-4 border-l-amber-600'
                            : 'hover:bg-amber-50'
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{session.stage?.title}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(session.start_date).toLocaleDateString('fr-BE')} au {new Date(session.end_date).toLocaleDateString('fr-BE')}
                        </p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                        isFull ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {session.current_registrations}/{session.capacity}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {dstWeek && availableSessions.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">Aucune autre session disponible pour cette selection.</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white rounded-b-xl">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleTransfer}
            disabled={!selectedDstSession || transferring}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              selectedDstSession && !transferring
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {transferring ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Transfert en cours...</>
            ) : (
              <><Repeat className="h-4 w-4" /> Transferer</>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
